import type { FC, ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";
import { tv } from "tailwind-variants";
import { Size } from "../helpers/helpers.js";
import { ButtonVariant } from "./button.js";

interface IconButtonProps {
  className?: string;
  icon: ReactNode;
  variant:
    | typeof ButtonVariant.Primary
    | typeof ButtonVariant.Secondary
    | typeof ButtonVariant.Tertiary;
  size?: Size;
  /** Set to `submit` when the button submits a surrounding form. */
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  isDisabled?: boolean;
  excludeFromTabOrder?: boolean;
  /** For accessibility and for browser automation. */
  accessibleLabel?: string;
}

export const iconButton = tv({
  // Without an explicit focus ring these fall back to the browser's default
  // outline, which doesn't match the rest of the app's focus treatment.
  base: "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:bg-slate-300 disabled:text-muted-foreground",
  variants: {
    variant: {
      [ButtonVariant.Primary]:
        "bg-primary text-primary-foreground shadow-xs hover:bg-indigo-500",
      [ButtonVariant.Secondary]:
        "bg-white shadow-xs ring-1 ring-border-strong ring-inset hover:bg-gray-50",
      [ButtonVariant.Tertiary]: "hover:bg-slate-200",
    },
    size: {
      [Size.SMALL]: "size-7",
      [Size.MEDIUM]: "size-8",
      [Size.LARGE]: "size-10",
    },
  },
  defaultVariants: {
    variant: ButtonVariant.Secondary,
    size: Size.SMALL,
  },
});

export const IconButton: FC<IconButtonProps> = ({
  className,
  icon,
  size = Size.SMALL,
  type,
  onClick,
  isDisabled,
  variant,
  excludeFromTabOrder,
  accessibleLabel,
}) => {
  return (
    <AriaButton
      className={iconButton({ variant, size, className })}
      type={type}
      isDisabled={isDisabled}
      onPress={onClick ? () => void onClick() : undefined}
      excludeFromTabOrder={excludeFromTabOrder}
      aria-label={accessibleLabel}
    >
      {icon}
    </AriaButton>
  );
};
